import React, { useEffect, useState } from 'react';
import Typography from '@material-ui/core/Typography';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import Button from '@material-ui/core/Button';
import {
  Collapse,
  Hidden,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useMediaQuery,
} from '@material-ui/core';
import { Edit, Remove } from '@material-ui/icons';
import { IUser, IUserGroup } from '../../../api-client/autogenerated';
import {
  getUsersFromUserGroup,
  ManagePermissionsDialogType,
} from '../../design/ManagePermissionsDialog';
import { getUserCompanyName } from '../../../scripts/utils';
import { patchProject } from '../../../models/api/project';
import { updateProject } from '../../../features/project/actions';
import { useDispatch, useSelector } from 'react-redux';
import { getCurrentSecurityGroup } from '../../../features/security/selectors';
import { getUserState } from '../../../features/user/selectors';
import { getProjectState } from '../../../features/project/selectors';
import { getGroupsState } from '../../../features/groups/selector';
import CreateUserGroupDialog from '../../design/CreateUserGroupDialog';
import Add from '@material-ui/icons/Add';
import IconButton from '@material-ui/core/IconButton';

export default function ProjectLifecycleNotificationGroup() {
  const dispatch = useDispatch();
  const security = useSelector(getCurrentSecurityGroup);
  const user = useSelector(getUserState);
  const project = useSelector(getProjectState);
  const groups = useSelector(getGroupsState);

  const isMobile = useMediaQuery('(max-width: 800px)');

  const [contentOpen, setContentOpen] = useState(false);
  const [createGroupDialogOpen, setCreateGroupDialogOpen] = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [selectedGroupUsers, setSelectedGroupUsers] = useState<IUser[]>();
  const [groupToEdit, setGroupToEdit] = useState<IUserGroup>();

  useEffect(() => {
    if (project?.projectLifecycleNotificationGroupId)
      setSelectedGroupId(project.projectLifecycleNotificationGroupId);
  }, [project]);

  useEffect(() => {
    if (selectedGroupId) {
      const group = groups.filter((group) => group.id === selectedGroupId)[0];
      setSelectedGroupUsers(getUsersFromUserGroup(group));
    } else {
      setSelectedGroupUsers(undefined);
    }
  }, [groups, selectedGroupId]);

  const handleAddGroup = (group: IUserGroup) => {
    if (groupToEdit) {
      setSelectedGroupUsers(getUsersFromUserGroup(group));
    } else {
      setSelectedGroupId(group.id);
    }
  };

  const handleChangeGroup = async () => {
    if (!project) return;
    await patchProject(project.id, { projectLifecycleNotificationGroupId: selectedGroupId });
    dispatch(updateProject({ projectLifecycleNotificationGroupId: selectedGroupId }));
  };

  const canCreateAndEditGroups = () => {
    return (
      user.isSiteAdmin ||
      user.adminOfSubscriberId === project?.subscriberId ||
      security?.canModifyUserGroups
    );
  };

  return (
    <>
      <Paper
        style={{
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0px 5px 15px rgb(0, 0, 0, .15)',
          padding: '20px',
          maxWidth: isMobile ? '100%' : 1000,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h2" style={{ padding: 0, marginBottom: 10 }}>
            Project Lifecycle Notification Group
          </Typography>
          <IconButton style={{ padding: 0 }}>
            {contentOpen ? (
              <Remove fontSize="large" htmlColor="#0947B9" onClick={() => setContentOpen(false)} />
            ) : (
              <Add fontSize="large" htmlColor="#0947B9" onClick={() => setContentOpen(true)} />
            )}
          </IconButton>
        </div>
        <Collapse in={contentOpen}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Select
              value={selectedGroupId}
              onChange={(event) => {
                setSelectedGroupId(event.target.value as string);
              }}
              style={{ height: 30 }}
              variant="outlined"
              displayEmpty
            >
              <MenuItem value="" disabled>
                Select Group
              </MenuItem>
              {groups.map((group) => (
                <MenuItem key={group.id} value={group.id}>
                  {group.name}
                </MenuItem>
              ))}
            </Select>
            <Button
              variant="contained"
              color="primary"
              disabled={
                (!project?.projectLifecycleNotificationGroupId && !selectedGroupId) ||
                project?.projectLifecycleNotificationGroupId === selectedGroupId
              }
              onClick={handleChangeGroup}
              style={{ marginLeft: 8, whiteSpace: 'nowrap' }}
            >
              Set Group
            </Button>
            <Hidden xsDown>
              <Button
                variant="contained"
                color="primary"
                disabled={!canCreateAndEditGroups()}
                onClick={() => setCreateGroupDialogOpen(true)}
                style={{ marginLeft: 8, whiteSpace: 'nowrap' }}
              >
                Create New Group
              </Button>
            </Hidden>
          </div>
          <Hidden smUp>
            <Button
              variant="contained"
              color="primary"
              disabled={!canCreateAndEditGroups()}
              onClick={() => setCreateGroupDialogOpen(true)}
              style={{ marginTop: 8, whiteSpace: 'nowrap', maxWidth: 200 }}
            >
              Create New Group
            </Button>
          </Hidden>
          {selectedGroupUsers && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', marginTop: 8 }}>
                <Typography variant="subtitle1" style={{ fontSize: 15 }}>
                  Notification List
                </Typography>
                <Button
                  onClick={() => {
                    setGroupToEdit(groups.find((g) => g.id === selectedGroupId));
                    setCreateGroupDialogOpen(true);
                  }}
                  disabled={!canCreateAndEditGroups()}
                  variant="contained"
                  color="primary"
                  startIcon={<Edit />}
                  style={{ padding: '6px 10px', marginLeft: 8 }}
                >
                  EDIT
                </Button>
              </div>
              <TableContainer style={{ overflowX: 'hidden', marginTop: 16 }}>
                <Table>
                  <TableHead>
                    <TableRow style={{ borderTop: '1px solid #EDECEC' }}>
                      <TableCell>Name</TableCell>
                      <TableCell>Company</TableCell>
                      <TableCell>Email</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedGroupUsers.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell>{user.name}</TableCell>
                        <TableCell>{getUserCompanyName(user, 'No company found')}</TableCell>
                        <TableCell>{user.email}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </Collapse>
      </Paper>
      <CreateUserGroupDialog
        open={createGroupDialogOpen}
        handleClose={() => {
          setCreateGroupDialogOpen(false);
          setGroupToEdit(undefined);
        }}
        handleAddGroup={handleAddGroup}
        type={ManagePermissionsDialogType.ProjectLifecycle}
        existingGroup={groupToEdit}
      />
    </>
  );
}
